// src/serviceOutage/ServiceOutageProvider.jsx
//
// Die React-Bindung des Erreichbarkeits-Waechters (UI2 Teil 9, Karte Z-1a).
//
// WAS ER TUT
// ----------
// Er liest den Modulzustand aus `frontend/src/api/serviceOutage.js` ueber
// `useSyncExternalStore` (React 18.2, der Hook ist verfuegbar) und stellt ihn
// als Kontext `{ zustand, seit, probiereJetzt }` bereit. Ausserdem feuert er
// beim UEBERGANG `ausfall -> online` GENAU EINMAL das Fensterereignis
// `pog:service-online` — daran haengt der Nachzug Z-1c (`useReloadOnRecovery`
// laedt die betroffenen Flaechen dann neu).
//
// 🔴 WARUM DAS EREIGNIS HIER UND NICHT IM MODUL STEHEN DARF
// --------------------------------------------------------
// Das Modul ist ein Singleton ohne React; es meldet Zustandswechsel ueber
// `abonniere()`. Das Ereignis gehoert an die React-Schicht, weil nur dort
// "Uebergang" (vorher/nachher) beobachtbar ist — und weil ZWEI Stellen es sonst
// feuern wuerden (Modul und Provider) und die Zusage "genau einmal" faellt.
//
// Kein eigener Timer: der Rueckzug liegt vollstaendig in `serviceOutage.js`.

import React, {
  createContext, useContext, useEffect, useMemo, useRef, useSyncExternalStore,
} from 'react';
import { abonniere, aktuellerZustand, seitWann, probiereJetzt } from '../api/serviceOutage';

const ServiceOutageContext = createContext({
  zustand: 'online',
  seit: null,
  probiereJetzt,
});

/**
 * Stellt den Erreichbarkeits-Zustand bereit und feuert `pog:service-online`
 * beim Uebergang `ausfall -> online`.
 *
 * @param {{ children: React.ReactNode }} props
 */
export function ServiceOutageProvider({ children }) {
  const zustand = useSyncExternalStore(abonniere, aktuellerZustand, aktuellerZustand);
  const vorher = useRef(zustand);

  useEffect(() => {
    const alt = vorher.current;
    vorher.current = zustand;
    if (alt === 'ausfall' && zustand === 'online') {
      try {
        window.dispatchEvent(new CustomEvent('pog:service-online'));
      } catch { /* sehr alte Browser ohne CustomEvent — dann gibt es kein Ereignis */ }
    }
  }, [zustand]);

  const wert = useMemo(
    () => ({ zustand, seit: seitWann(), probiereJetzt }),
    [zustand],
  );

  return (
    <ServiceOutageContext.Provider value={wert}>
      {children}
    </ServiceOutageContext.Provider>
  );
}

/** Der Zustand des Erreichbarkeits-Waechters: `{ zustand, seit, probiereJetzt }`. */
export function useServiceOutage() {
  return useContext(ServiceOutageContext);
}
